import { useState } from "react";
import { LEGAL_VERSION } from "@usales/contract/values";
import { api, ApiError, type Me } from "../api.ts";
import { LEGAL_PATHS } from "../legal.tsx";

/**
 * 登录。**这是唯一一屏会被没登录的人看到的**,所以它同时是产品的第一印象。
 *
 * 左边那块不是装饰:它讲的是北极星的三条核心命题(数据/阶段/动作)。
 * 一个还没进去的人看到的如果只是「邮箱 / 口令 / 登录」,那他对这个产品的
 * 全部认知就是"又一个后台"。窄屏下这块直接收起来 —— 手机上它抢的是输入框的位置。
 *
 * 会话走 httpOnly cookie,**前端拿不到也不存令牌** —— 所以这里没有任何
 * localStorage 操作。XSS 偷不走一个 JS 读不到的东西,这是比「存 localStorage
 * 再小心别 XSS」可靠得多的做法。
 */

/** 左侧讲什么。取自北极星的四条核心命题,不是填充文案。 */
const CLAIMS = [
  ["每次沟通，自动整理", "从录音、聊天与会议资料提取客户需求和关键线索"],
  ["客户进展，清晰可见", "结合沟通判断阶段，为团队保留可核对的依据"],
  ["下一步行动，提前准备", "建议、知识和跟进草稿集中在同一个工作空间"],
] as const;

export function Login({ onDone }: { onDone: (me: Me) => void }): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [tenants, setTenants] = useState<string[]>([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // 不预先勾上:默认勾选的「同意」在个保法和工信部的检查口径里都不算同意
  const [agreed, setAgreed] = useState(false);

  const submit = async (): Promise<void> => {
    setErr(""); setBusy(true);
    try {
      const r = await api.post<{ user: Me }>("/api/auth/login", {
        email, password, acceptedTerms: LEGAL_VERSION, ...(tenantId ? { tenantId } : {}),
      });
      onDone(r.user);
    } catch (e) {
      // 409 = 同邮箱存在于多个组织,让用户选一个
      if (e instanceof ApiError && e.status === 409 && e.tenants) {
        setTenants(e.tenants);
        setErr("该邮箱在多个组织下存在,请选择要登录的组织");
      } else {
        setErr(String((e as Error).message));
      }
    } finally { setBusy(false); }
  };

  const ready = email.trim() && password && agreed && (tenants.length === 0 || tenantId);

  return (
    <div className="login">
      <aside className="login-brand">
        <div className="login-brand-content">
          <a className="login-logo" href="/" title="返回首页">Usales <span>元销</span></a>
          <p className="login-tagline">
            让每一次沟通，<br />都成为客户推进的下一步。
          </p>
          <ul className="login-claims">
            {CLAIMS.map(([t, d]) => (
              <li key={t}>
                <b>{t}</b>
                <span>{d}</span>
              </li>
            ))}
          </ul>
          <div className="login-foot">客户进展 · 团队知识 · AI 行动建议</div>
        </div>
      </aside>

      <main className="login-pane">
        <form className="login-form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <h1 className="login-company-name">杭州网利信息技术有限公司</h1>
          <div className="login-product-name">Usales <span>元销</span><small>销售协作平台</small></div>
          <p className="muted">登录你的销售协作工作空间</p>

          {err && <div className="err">{err}</div>}

          <label htmlFor="email">邮箱</label>
          <input
            id="email" className="in" type="text" autoComplete="username"
            placeholder="you@company.com" value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label htmlFor="pw">密码</label>
          <input
            id="pw" className="in" type="password" autoComplete="current-password"
            placeholder="••••••••" value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {tenants.length > 0 && (
            <>
              <label htmlFor="t">组织</label>
              <select id="t" className="in" value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}>
                <option value="">请选择</option>
                {tenants.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </>
          )}

          <label className="login-agree">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span>我已阅读并同意<a href={LEGAL_PATHS.terms} target="_blank" rel="noopener">《用户协议》</a>和<a href={LEGAL_PATHS.privacy} target="_blank" rel="noopener">《隐私政策》</a></span>
          </label>

          <button className="btn login-submit" type="submit" disabled={!ready || busy}>
            {busy ? "登录中…" : "登录"}
          </button>

          <p className="login-hint">
            账号由团队管理员开通。如需加入，请联系管理员。
          </p>
        </form>
      </main>
    </div>
  );
}
